/**
 * Coach search-engine indexability predicate (aka SEO visibility, robots
 * noindex rule). Single source of truth for whether a coach's public pages
 * (profile, offers, discovery presentation) may be indexed and included in
 * sitemaps.
 *
 * A coach is indexable only when the profile is publicly visible at all (see
 * `isCoachPubliclyHidden` — public toggle on, not a pre-generated
 * placeholder), they are marketplace-approved, and they have not opted out of
 * search indexing. Deriving it from the visibility predicate keeps the
 * invariant "a page we 404 is never advertised as indexable" structural.
 *
 * Deliberately NO profile-photo requirement: `profilePictureUrl` is sometimes
 * `""` rather than NULL, and requiring it would silently de-index real
 * coaches with a failed upload.
 *
 * Safe to import from client code — route `head()` predicates run on the
 * client too, so this must stay free of server-only imports.
 */
import { isCoachPubliclyHidden } from "~/lib/shared/coach-visibility";

/** Minimal structural shape of the coach data the predicate needs. */
export interface CoachIndexabilityInput {
  marketplaceApprovedAt: Date | string | null;
  profile:
    | {
        isPublic: boolean;
        isPreGeneratedProfile: boolean;
        searchIndexingAllowed: boolean;
      }
    | null
    | undefined;
}

export function isCoachIndexable(coach: CoachIndexabilityInput): boolean {
  const { profile } = coach;
  // The `!profile` arm is redundant with the hidden predicate at runtime; it
  // is what narrows `profile` for the field access below.
  if (!profile || isCoachPubliclyHidden(profile)) {
    return false;
  }
  return coach.marketplaceApprovedAt != null && profile.searchIndexingAllowed;
}
